/**
 * Settings - Admin only
 * - Change panel name
 * - Set default reseller restrictions
 * - Cloudflare Turnstile (CAPTCHA) on login
 */
import React, { useEffect, useState } from "react";
import { getSettings, updateSettings, getAdminBackup, postAdminRestore, createServerBackup, listServerBackups, deleteServerBackup, downloadServerBackup } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import {
  Save,
  Settings as SettingsIcon,
  ShieldAlert,
  Cloud,
  ExternalLink,
  CheckCircle2,
  KeyRound,
  MessageCircle,
  Download,
  Upload,
  Database,
  Loader2,
  AlertTriangle,
  HardDrive,
  Trash2,
  RefreshCw,
} from "lucide-react";

const SETTINGS_TIDS = {
  panelName: "settings-panel-name",
  defaultMaxKeys: "settings-default-max-keys",
  defaultMaxPlaylists: "settings-default-max-playlists",
  defaultCanManageDevices: "settings-default-can-manage-devices",
  defaultCanSendNotifs: "settings-default-can-send-notifs",
  turnstileEnabled: "settings-turnstile-enabled",
  turnstileSiteKey: "settings-turnstile-site-key",
  turnstileSecretKey: "settings-turnstile-secret-key",
  keyValidityDays: "settings-key-validity-days",
  keyCreditCost: "settings-key-credit-cost",
  trialValidityDays: "settings-trial-validity-days",
  trialCreditCost: "settings-trial-credit-cost",
  supportWhatsapp: "settings-support-whatsapp",
  btnSave: "settings-btn-save",
};

export default function Settings() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    panel_name: "ZA Player Pro Admin",
    default_reseller_max_keys: "",
    default_reseller_max_playlists: "",
    default_reseller_can_manage_devices: true,
    default_reseller_can_send_notifications: false,
    turnstile_enabled: false,
    turnstile_site_key: "",
    turnstile_secret_key: "",
    key_validity_days: 0,
    key_credit_cost: 1,
    trial_validity_days: 5,
    trial_credit_cost: 0,
    support_whatsapp: "",
    // TMDB v3 — never echoed back; populated only when admin types a new
    // value. `tmdb_api_key_set` reflects whether one is already stored.
    tmdb_api_key: "",
    // Global User-Agent the player apps send on every HTTP request
    // (catalog fetch, streams, EPG, OTA). Empty means "use built-in
    // default". Echoed back (NOT a secret) so the admin can see the
    // current value.
    app_user_agent: "",
    // v1.3.37 — auto-delete inactive playlists + their bound activation
    // keys after N days of no /api/player/playlists fetch. Hard-coded
    // 5-day grace period is added on top server-side.
    auto_delete_inactive_playlists: false,
    auto_delete_inactive_days: 30,
  });
  // Track if the secret key was already set (don't overwrite with empty)
  const [secretKeyConfigured, setSecretKeyConfigured] = useState(false);
  const [tmdbKeyConfigured, setTmdbKeyConfigured] = useState(false);

  useEffect(() => {
    getSettings()
      .then((s) => {
        setForm({
          panel_name: s.panel_name || "ZA Player Pro Admin",
          default_reseller_max_keys: s.default_reseller_max_keys ?? "",
          default_reseller_max_playlists: s.default_reseller_max_playlists ?? "",
          default_reseller_can_manage_devices: s.default_reseller_can_manage_devices,
          default_reseller_can_send_notifications: s.default_reseller_can_send_notifications,
          turnstile_enabled: !!s.turnstile_enabled,
          turnstile_site_key: s.turnstile_site_key || "",
          turnstile_secret_key: "", // never echo secret to client; show placeholder if set
          key_validity_days: s.key_validity_days ?? 0,
          key_credit_cost: s.key_credit_cost ?? 1,
          trial_validity_days: s.trial_validity_days ?? 5,
          trial_credit_cost: s.trial_credit_cost ?? 0,
          support_whatsapp: s.support_whatsapp ?? "",
          tmdb_api_key: "", // never echo secrets to client
          app_user_agent: s.app_user_agent || "",
          auto_delete_inactive_playlists: !!s.auto_delete_inactive_playlists,
          auto_delete_inactive_days: s.auto_delete_inactive_days ?? 30,
        });
        setSecretKeyConfigured(!!s.turnstile_secret_key);
        setTmdbKeyConfigured(!!s.tmdb_api_key);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        panel_name: form.panel_name,
        default_reseller_max_keys:
          form.default_reseller_max_keys === ""
            ? null
            : parseInt(form.default_reseller_max_keys, 10),
        default_reseller_max_playlists:
          form.default_reseller_max_playlists === ""
            ? null
            : parseInt(form.default_reseller_max_playlists, 10),
        default_reseller_can_manage_devices: form.default_reseller_can_manage_devices,
        default_reseller_can_send_notifications:
          form.default_reseller_can_send_notifications,
        turnstile_enabled: form.turnstile_enabled,
        turnstile_site_key: form.turnstile_site_key || null,
        key_validity_days: Math.max(0, parseInt(form.key_validity_days, 10) || 0),
        key_credit_cost: Math.max(1, parseInt(form.key_credit_cost, 10) || 1),
        trial_validity_days: Math.max(1, parseInt(form.trial_validity_days, 10) || 1),
        trial_credit_cost: Math.max(0, parseInt(form.trial_credit_cost, 10) || 0),
        support_whatsapp: form.support_whatsapp || "",
      };
      // Only send secret key if user typed something (otherwise keep existing)
      if (form.turnstile_secret_key) {
        payload.turnstile_secret_key = form.turnstile_secret_key;
      }
      // Only update the TMDB key when the admin actually typed a new value.
      if (form.tmdb_api_key) {
        payload.tmdb_api_key = form.tmdb_api_key;
      }
      // User-Agent override — always send (empty string means "clear,
      // use default"). NOT a secret so it's safe to round-trip.
      payload.app_user_agent = form.app_user_agent || "";
      payload.auto_delete_inactive_playlists = !!form.auto_delete_inactive_playlists;
      payload.auto_delete_inactive_days = Math.max(1, parseInt(form.auto_delete_inactive_days, 10) || 30);
      await updateSettings(payload);
      toast({
        title: "Settings saved",
        description: "Refresh to apply panel name changes.",
      });
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      toast({
        title: "Error",
        description: e.response?.data?.detail || e.message,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-slate-500">Loading...</div>;

  const turnstileReady =
    form.turnstile_enabled &&
    form.turnstile_site_key &&
    (secretKeyConfigured || form.turnstile_secret_key);

  return (
    <div className="space-y-6 max-w-3xl">
      <p className="text-slate-600">
        Configure the admin panel, security, and default restrictions for new resellers.
      </p>

      {/* Panel name */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <SettingsIcon className="w-4 h-4 text-orange-500" />
            Panel
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>Panel Name</Label>
            <Input
              data-testid={SETTINGS_TIDS.panelName}
              value={form.panel_name}
              onChange={(e) => setForm({ ...form, panel_name: e.target.value })}
              placeholder="ZA Player Pro Admin"
              className="max-w-md"
            />
            <p className="text-xs text-slate-500 mt-1">
              Displayed on the login page and sidebar header.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Activation Key Policy */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-orange-500" />
            Activation Key Policy
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            Default validity period and credit cost applied when an activation key is
            generated. Admins do not consume credits.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl">
            <div>
              <Label>Activation valid for (days)</Label>
              <Input
                data-testid={SETTINGS_TIDS.keyValidityDays}
                type="number"
                min="0"
                value={form.key_validity_days}
                onChange={(e) =>
                  setForm({ ...form, key_validity_days: e.target.value })
                }
                placeholder="0 = lifetime"
              />
              <p className="text-xs text-slate-500 mt-1">
                <span className="font-medium">0</span> = lifetime (no expiry).
                Example: <span className="font-mono">30</span>, <span className="font-mono">365</span>.
              </p>
            </div>
            <div>
              <Label>Credits per key</Label>
              <Input
                data-testid={SETTINGS_TIDS.keyCreditCost}
                type="number"
                min="1"
                value={form.key_credit_cost}
                onChange={(e) =>
                  setForm({ ...form, key_credit_cost: e.target.value })
                }
                placeholder="1"
              />
              <p className="text-xs text-slate-500 mt-1">
                Number of credits a reseller spends to generate one key. Minimum
                <span className="font-mono"> 1</span>.
              </p>
            </div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-900">
            Changes apply to <span className="font-semibold">newly generated keys</span> only.
            Existing keys keep their original expiry.
          </div>

          {/* Trial / Test activation */}
          <div className="pt-4 border-t border-slate-200">
            <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
              Test / Trial activation
            </h4>
            <p className="text-xs text-slate-500 mt-1 mb-3">
              Used when an admin or reseller clicks <span className="font-semibold">Generate Test Key</span> — lets clients evaluate the player before paying.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl">
              <div>
                <Label>Trial valid for (days)</Label>
                <Input
                  data-testid={SETTINGS_TIDS.trialValidityDays}
                  type="number"
                  min="1"
                  value={form.trial_validity_days}
                  onChange={(e) =>
                    setForm({ ...form, trial_validity_days: e.target.value })
                  }
                  placeholder="5"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Default <span className="font-mono">5</span> days. Minimum
                  <span className="font-mono"> 1</span>.
                </p>
              </div>
              <div>
                <Label>Trial credit cost</Label>
                <Input
                  data-testid={SETTINGS_TIDS.trialCreditCost}
                  type="number"
                  min="0"
                  value={form.trial_credit_cost}
                  onChange={(e) =>
                    setForm({ ...form, trial_credit_cost: e.target.value })
                  }
                  placeholder="0"
                />
                <p className="text-xs text-slate-500 mt-1">
                  <span className="font-mono">0</span> = free trial. Set
                  &gt; 0 to charge resellers per trial.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* v1.3.37 — Auto-delete inactive playlists. When enabled, the
          server background janitor scans for playlists no device has
          requested in N days and removes them along with every
          activation key still bound to them. A hard-coded 5-day grace
          period is added on top so users have time to renew. */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Trash2 className="w-4 h-4 text-rose-500" />
            Auto-delete inactive playlists
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            Automatically delete playlists no device has requested in the configured number of days.
            Activation keys bound to those playlists are deleted as well. A built-in 5-day grace period is added on top so users have time to renew.
          </p>
        </CardHeader>
        <CardContent className="space-y-3 max-w-xl">
          <div className="flex items-center justify-between gap-4 p-3 rounded-md bg-slate-50 border border-slate-200">
            <div className="min-w-0">
              <p className="text-sm font-medium">Enable auto-delete</p>
              <p className="text-xs text-slate-500 mt-0.5">
                Off by default. Turn on and pick a day threshold below.
              </p>
            </div>
            <Switch
              data-testid="settings-auto-delete-toggle"
              checked={!!form.auto_delete_inactive_playlists}
              onCheckedChange={(v) =>
                setForm({ ...form, auto_delete_inactive_playlists: !!v })
              }
            />
          </div>
          <div>
            <Label>Inactive after</Label>
            <div className="flex items-center gap-2">
              <Input
                data-testid="settings-auto-delete-days"
                type="number"
                min="1"
                value={form.auto_delete_inactive_days}
                onChange={(e) =>
                  setForm({ ...form, auto_delete_inactive_days: e.target.value })
                }
                disabled={!form.auto_delete_inactive_playlists}
                className="max-w-[120px]"
              />
              <span className="text-sm text-slate-600">days</span>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Effective cutoff: <span className="font-mono">{Math.max(1, parseInt(form.auto_delete_inactive_days, 10) || 1) + 5}</span> days (including the 5-day grace period).
              The janitor runs every hour.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Support contact (WhatsApp) */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-emerald-500" />
            Support contact
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            Shown to end-users on the activation screen as a floating WhatsApp button (bottom right). Leave blank to hide.
          </p>
        </CardHeader>
        <CardContent className="space-y-3 max-w-xl">
          <div>
            <Label>WhatsApp number</Label>
            <Input
              data-testid={SETTINGS_TIDS.supportWhatsapp}
              type="tel"
              value={form.support_whatsapp}
              onChange={(e) => setForm({ ...form, support_whatsapp: e.target.value })}
              placeholder="+1 555 123 4567"
            />
            <p className="text-xs text-slate-500 mt-1">
              Include country code. The player will open <span className="font-mono">https://wa.me/&lt;digits&gt;</span> when tapped.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* TMDB API integration — fills missing movie/series metadata
          (poster / plot / cast / rating / backdrop) using the provider's
          tmdb_id. Stored server-side and proxied via /api/tmdb so the
          key is never embedded in the APK. */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Cloud className="w-4 h-4 text-amber-500" />
            TMDB metadata
            {tmdbKeyConfigured && (
              <span className="text-[10px] uppercase tracking-wider text-emerald-600 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded">
                Key configured
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 max-w-xl">
          <div>
            <Label>TMDB v3 API key</Label>
            <Input
              data-testid="settings-tmdb-api-key"
              type="password"
              value={form.tmdb_api_key}
              onChange={(e) => setForm({ ...form, tmdb_api_key: e.target.value })}
              placeholder={tmdbKeyConfigured ? "•••••••••• (already configured — type to replace)" : "Paste your TMDB API Read Access Token (eyJ…) or v3 hex key"}
            />
            <p className="text-xs text-slate-500 mt-1">
              Get a free key at{" "}
              <a
                href="https://www.themoviedb.org/settings/api"
                target="_blank"
                rel="noreferrer"
                className="text-orange-500 underline"
              >
                themoviedb.org/settings/api
              </a>
              . Either the long Bearer token (starts with <span className="font-mono">eyJ</span>)
              or the 32-char hex v3 key works. The key stays on the server — the
              APK calls <span className="font-mono">/api/tmdb/movie/&#123;id&#125;</span> and
              <span className="font-mono"> /api/tmdb/tv/&#123;id&#125;</span> via the backend
              proxy, using the provider-supplied <span className="font-mono">tmdb_id</span> to
              fill in missing posters / plot / cast / rating.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Player User-Agent override — many IPTV providers gate live
          streams by User-Agent string. Setting this here propagates
          to EVERY device through /api/public/player-config and is
          applied to the Capacitor WebView, ExoPlayer, libVLC, and
          the OTA APK downloader in one shot. Empty = built-in
          default. */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Cloud className="w-4 h-4 text-purple-500" />
            Player User-Agent
            {form.app_user_agent && (
              <span className="text-[10px] uppercase tracking-wider text-emerald-600 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded">
                Override active
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 max-w-xl">
          <div>
            <Label>User-Agent string</Label>
            <Input
              data-testid="settings-app-user-agent"
              value={form.app_user_agent}
              onChange={(e) => setForm({ ...form, app_user_agent: e.target.value })}
              placeholder="Leave empty to use built-in default"
              className="font-mono text-xs"
            />
            <div className="text-xs text-slate-500 mt-1 space-y-1">
              <p>
                Sent on every outbound request from every player: Web
                Player (via server-side proxy), Windows Web Player kiosk
                (native session interceptor — even for direct HLS
                segments), and the Android APK (ExoPlayer + libVLC +
                OTA downloader). Provider sees the same UA regardless
                of which client is streaming.
              </p>
              <p>
                <span className="font-semibold text-slate-700">Try these when a stream won&apos;t play:</span>
              </p>
              <ul className="list-disc list-inside ml-1 font-mono text-[11px] space-y-0.5">
                <li
                  className="cursor-pointer hover:text-orange-600"
                  onClick={() => setForm({ ...form, app_user_agent: "VLC/3.0.20 LibVLC/3.0.20" })}
                  data-testid="ua-preset-vlc"
                >
                  VLC/3.0.20 LibVLC/3.0.20
                </li>
                <li
                  className="cursor-pointer hover:text-orange-600"
                  onClick={() => setForm({ ...form, app_user_agent: "Mozilla/5.0 (SmartTV; Linux; Tizen 6.0) AppleWebKit/537.36" })}
                  data-testid="ua-preset-smarttv"
                >
                  Mozilla/5.0 (SmartTV; Linux; Tizen 6.0) AppleWebKit/537.36
                </li>
                <li
                  className="cursor-pointer hover:text-orange-600"
                  onClick={() => setForm({ ...form, app_user_agent: "Lavf/58.76.100" })}
                  data-testid="ua-preset-ffmpeg"
                >
                  Lavf/58.76.100
                </li>
                <li
                  className="cursor-pointer hover:text-orange-600"
                  onClick={() => setForm({ ...form, app_user_agent: "IPTVSmarters" })}
                  data-testid="ua-preset-iptvsmarters"
                >
                  IPTVSmarters
                </li>
              </ul>
              <p className="pt-1">
                Click any preset to populate the field, then Save.
                Devices pick up the new UA on next launch (or after
                pulling fresh playlist data).
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Backup / Restore — exports everything operator-facing (keys,
          playlists, resellers + credits, devices, key↔playlist
          assignments, app settings) as a single JSON file. Re-uploading
          that file restores the state. Useful for migrating servers or
          rolling back after a bad bulk operation. */}
      <BackupRestoreCard toast={toast} />

      {/* Cloudflare Turnstile */}      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Cloud className="w-4 h-4 text-orange-500" />
            Cloudflare Turnstile
            {turnstileReady && (
              <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-normal bg-emerald-50 px-2 py-0.5 rounded-full">
                <CheckCircle2 className="w-3 h-3" />
                Active
              </span>
            )}
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            Free CAPTCHA-style bot protection for your login page. No user interaction
            required in most cases.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between bg-slate-50 px-3 py-2 rounded-md">
            <div>
              <Label className="text-sm">Enable Turnstile on login</Label>
              <p className="text-xs text-slate-500">
                Requires Site Key and Secret Key from your Cloudflare dashboard.
              </p>
            </div>
            <Switch
              data-testid={SETTINGS_TIDS.turnstileEnabled}
              checked={form.turnstile_enabled}
              onCheckedChange={(v) => setForm({ ...form, turnstile_enabled: v })}
            />
          </div>

          <div>
            <Label>Site Key</Label>
            <Input
              data-testid={SETTINGS_TIDS.turnstileSiteKey}
              value={form.turnstile_site_key}
              onChange={(e) => setForm({ ...form, turnstile_site_key: e.target.value })}
              placeholder="0x4AAAAAAA..."
              className="font-mono text-sm"
            />
          </div>

          <div>
            <Label>
              Secret Key{" "}
              {secretKeyConfigured && (
                <span className="text-emerald-700 text-xs ml-1">(configured · leave blank to keep)</span>
              )}
            </Label>
            <Input
              data-testid={SETTINGS_TIDS.turnstileSecretKey}
              type="password"
              value={form.turnstile_secret_key}
              onChange={(e) => setForm({ ...form, turnstile_secret_key: e.target.value })}
              placeholder={secretKeyConfigured ? "•••••••• (set, leave blank to keep)" : "0x4AAAAAAA..."}
              className="font-mono text-sm"
            />
          </div>

          {/* Setup Instructions */}
          <div className="bg-sky-50 border border-sky-200 rounded-md p-4">
            <h4 className="font-semibold text-sm text-sky-900 mb-2 flex items-center gap-2">
              <Cloud className="w-4 h-4" />
              Setup Instructions
            </h4>
            <ol className="text-xs text-sky-900 space-y-1.5 list-decimal list-inside ml-1">
              <li>
                Go to{" "}
                <a
                  href="https://dash.cloudflare.com/?to=/:account/turnstile"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sky-700 underline inline-flex items-center gap-0.5"
                >
                  Cloudflare Turnstile dashboard
                  <ExternalLink className="w-3 h-3" />
                </a>{" "}
                and sign in (free account).
              </li>
              <li>
                Click <span className="font-semibold">Add Site</span>. Give it a name (e.g.{" "}
                <span className="font-mono">ZA Player Pro Admin</span>) and enter your
                admin panel hostname (e.g.{" "}
                <span className="font-mono">{typeof window !== "undefined" ? window.location.hostname : "yourdomain.com"}</span>
                ).
              </li>
              <li>
                Choose <span className="font-semibold">Managed</span> widget mode (auto
                challenge). Click <span className="font-semibold">Create</span>.
              </li>
              <li>
                Copy the <span className="font-semibold">Site Key</span> and paste it
                above.
              </li>
              <li>
                Copy the <span className="font-semibold">Secret Key</span> and paste it
                above.
              </li>
              <li>
                Toggle{" "}
                <span className="font-semibold">Enable Turnstile on login</span> ON and
                click <span className="font-semibold">Save Settings</span>.
              </li>
              <li>
                Reload the login page — you should see the Cloudflare challenge widget.
              </li>
            </ol>
            <p className="text-[11px] text-sky-700 mt-3 italic">
              Tip: Cloudflare provides free <a className="underline" href="https://developers.cloudflare.com/turnstile/troubleshooting/testing/" target="_blank" rel="noopener noreferrer">test keys</a> (always-pass / always-fail / always-block) for development.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Reseller restrictions */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-orange-500" />
            Default Reseller Restrictions
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            Pre-filled when creating a new reseller. Override per-user in the Users page.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3 max-w-md">
            <div>
              <Label>Max Keys cap</Label>
              <Input
                data-testid={SETTINGS_TIDS.defaultMaxKeys}
                type="number"
                min="0"
                value={form.default_reseller_max_keys}
                onChange={(e) =>
                  setForm({ ...form, default_reseller_max_keys: e.target.value })
                }
                placeholder="Unlimited"
              />
            </div>
            <div>
              <Label>Max Playlists</Label>
              <Input
                data-testid={SETTINGS_TIDS.defaultMaxPlaylists}
                type="number"
                min="0"
                value={form.default_reseller_max_playlists}
                onChange={(e) =>
                  setForm({ ...form, default_reseller_max_playlists: e.target.value })
                }
                placeholder="Unlimited"
              />
            </div>
          </div>
          <div className="space-y-3 pt-2">
            <div className="flex items-center justify-between bg-slate-50 px-3 py-2 rounded-md max-w-md">
              <div>
                <Label className="text-sm">Can manage devices</Label>
                <p className="text-xs text-slate-500">Block / unblock / delete devices</p>
              </div>
              <Switch
                data-testid={SETTINGS_TIDS.defaultCanManageDevices}
                checked={form.default_reseller_can_manage_devices}
                onCheckedChange={(v) =>
                  setForm({ ...form, default_reseller_can_manage_devices: v })
                }
              />
            </div>
            <div className="flex items-center justify-between bg-slate-50 px-3 py-2 rounded-md max-w-md">
              <div>
                <Label className="text-sm">Can send notifications</Label>
                <p className="text-xs text-slate-500">Push messages to player apps</p>
              </div>
              <Switch
                data-testid={SETTINGS_TIDS.defaultCanSendNotifs}
                checked={form.default_reseller_can_send_notifications}
                onCheckedChange={(v) =>
                  setForm({ ...form, default_reseller_can_send_notifications: v })
                }
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Button
        data-testid={SETTINGS_TIDS.btnSave}
        onClick={handleSave}
        disabled={saving}
        className="bg-orange-500 hover:bg-orange-600"
      >
        <Save className="w-4 h-4 mr-2" />
        {saving ? "Saving..." : "Save Settings"}
      </Button>
    </div>
  );
}


// ---------------------------------------------------------------------------
// BackupRestoreCard — downloads the full snapshot as a timestamped JSON file
// and accepts a previously-downloaded file via a file input. Lives in this
// file to keep the entire Settings page in one module.
// ---------------------------------------------------------------------------
function BackupRestoreCard({ toast }) {
  const [exporting, setExporting] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [wipeFirst, setWipeFirst] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const fileInputRef = React.useRef(null);
  // Server-side rolling snapshots — distinct from the in-browser
  // download flow above. The list refreshes after Create / Delete.
  const [serverBackups, setServerBackups] = useState([]);
  const [serverBackupsLoading, setServerBackupsLoading] = useState(false);
  const [creatingServer, setCreatingServer] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const refreshServerBackups = React.useCallback(async () => {
    setServerBackupsLoading(true);
    try {
      const r = await listServerBackups();
      setServerBackups(Array.isArray(r?.items) ? r.items : []);
    } catch (e) {
      // Silent — the section just shows "no backups yet" on first
      // visit before a backend rolls forward.
    } finally {
      setServerBackupsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    refreshServerBackups();
  }, [refreshServerBackups]);

  const handleCreateServerBackup = async () => {
    setCreatingServer(true);
    try {
      const meta = await createServerBackup();
      toast({
        title: "Backup created on server",
        description: `${meta.counts?.keys || 0} keys · ${meta.counts?.playlists || 0} playlists · ${(meta.size_bytes / 1024).toFixed(1)} KB`,
      });
      await refreshServerBackups();
    } catch (e) {
      toast({
        title: "Couldn't create backup",
        description: e?.response?.data?.detail || e.message,
        variant: "destructive",
      });
    } finally {
      setCreatingServer(false);
    }
  };

  const handleDownloadServerBackup = async (b) => {
    try {
      const blob = await downloadServerBackup(b.id);
      const url = URL.createObjectURL(blob);
      const stamp = b.created_at.replace(/[:.]/g, "-").slice(0, 19);
      const a = document.createElement("a");
      a.href = url;
      a.download = `zaplayerpro-backup-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast({
        title: "Download failed",
        description: e?.response?.data?.detail || e.message,
        variant: "destructive",
      });
    }
  };

  const handleDeleteServerBackup = async (b) => {
    if (!window.confirm(`Delete server backup from ${b.created_at}? This cannot be undone.`)) return;
    setDeletingId(b.id);
    try {
      await deleteServerBackup(b.id);
      toast({ title: "Backup deleted" });
      await refreshServerBackups();
    } catch (e) {
      toast({
        title: "Couldn't delete backup",
        description: e?.response?.data?.detail || e.message,
        variant: "destructive",
      });
    } finally {
      setDeletingId(null);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const data = await getAdminBackup();
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      const a = document.createElement("a");
      a.href = url;
      a.download = `zaplayerpro-backup-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast({
        title: "Backup downloaded",
        description: `${data.counts.keys} keys · ${data.counts.playlists} playlists · ${data.counts.resellers || 0} resellers · ${data.counts.devices || 0} devices`,
      });
    } catch (e) {
      toast({
        title: "Backup failed",
        description: e?.response?.data?.detail || e.message,
        variant: "destructive",
      });
    } finally {
      setExporting(false);
    }
  };

  const handleRestoreFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setRestoring(true);
    setLastResult(null);
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      if (!parsed?.keys && !parsed?.playlists) {
        throw new Error("File doesn't look like a ZA Player Pro backup");
      }
      const payload = {
        wipe_first: wipeFirst,
        keys: parsed.keys || [],
        playlists: parsed.playlists || [],
        resellers: parsed.resellers || [],
        assignments: parsed.assignments || [],
        devices: parsed.devices || [],
        settings: parsed.settings || null,
      };
      const result = await postAdminRestore(payload);
      setLastResult(result);
      toast({
        title: "Restore complete",
        description: `Keys ${result.keys_restored} · Playlists ${result.playlists_restored} · Resellers ${result.resellers_restored || 0}`,
      });
    } catch (err) {
      toast({
        title: "Restore failed",
        description: err?.response?.data?.detail || err.message,
        variant: "destructive",
      });
    } finally {
      setRestoring(false);
      // Reset the input so the same file can be re-selected if needed.
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Database className="w-4 h-4 text-emerald-500" />
          Backup &amp; Restore
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 max-w-xl">
        <p className="text-sm text-slate-600 leading-relaxed">
          Export a single JSON file containing every activation key,
          playlist, reseller (with their credits), device activation, and
          app settings. Re-upload the file later to restore. Admin accounts
          are never exported or modified.
        </p>

        <div className="flex flex-wrap gap-2">
          <Button
            data-testid="settings-backup-create-server"
            onClick={handleCreateServerBackup}
            disabled={creatingServer}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {creatingServer ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <HardDrive className="w-4 h-4 mr-2" />
            )}
            {creatingServer ? "Creating…" : "Create backup"}
          </Button>

          <Button
            data-testid="settings-backup-download"
            onClick={handleExport}
            disabled={exporting}
            variant="outline"
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            {exporting ? "Preparing…" : "Download backup"}
          </Button>

          <Button
            data-testid="settings-backup-restore"
            onClick={() => fileInputRef.current?.click()}
            disabled={restoring}
            variant="outline"
            className="border-amber-300 text-amber-700 hover:bg-amber-50"
          >
            {restoring ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            {restoring ? "Restoring…" : "Restore from file…"}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json,.json"
            onChange={handleRestoreFile}
            className="hidden"
            data-testid="settings-backup-file-input"
          />
        </div>

        {/* Server-side snapshot list — populated after the first
            "Create backup" click. Survives admin browser sessions
            (unlike the local Download backup which only lives on the
            admin's machine). Useful for "create a snapshot before I
            try this risky bulk import" workflows. */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-xs uppercase tracking-wider font-semibold text-slate-500">
              Saved on server ({serverBackups.length})
            </h4>
            <Button
              variant="ghost"
              size="sm"
              onClick={refreshServerBackups}
              disabled={serverBackupsLoading}
              className="h-7 px-2 text-xs"
              data-testid="settings-backup-refresh-server"
            >
              <RefreshCw className={`w-3 h-3 mr-1 ${serverBackupsLoading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
          {serverBackups.length === 0 ? (
            <div className="text-xs text-slate-500 italic py-2">
              No server-side backups yet. Click <span className="font-semibold">Create backup</span> to save a snapshot.
            </div>
          ) : (
            <div className="border border-slate-200 rounded-md divide-y divide-slate-100 max-h-64 overflow-y-auto">
              {serverBackups.map((b) => (
                <div
                  key={b.id}
                  className="flex items-center justify-between px-3 py-2 text-xs hover:bg-slate-50"
                  data-testid={`backup-row-${b.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-slate-800 truncate">
                      {b.created_at.replace("T", " ").slice(0, 19)} UTC
                    </div>
                    <div className="text-slate-500 mt-0.5">
                      {b.counts?.keys || 0} keys · {b.counts?.playlists || 0} playlists ·{" "}
                      {b.counts?.resellers || 0} resellers ·{" "}
                      {(b.size_bytes / 1024).toFixed(1)} KB
                      {b.created_by && <span> · by {b.created_by}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDownloadServerBackup(b)}
                      className="h-7 px-2"
                      title="Download this snapshot as JSON"
                      data-testid={`backup-download-${b.id}`}
                    >
                      <Download className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={deletingId === b.id}
                      onClick={() => handleDeleteServerBackup(b)}
                      className="h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                      title="Delete this snapshot"
                      data-testid={`backup-delete-${b.id}`}
                    >
                      {deletingId === b.id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="w-3.5 h-3.5" />
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-start gap-3 p-3 rounded-md border border-amber-200 bg-amber-50">
          <Switch
            checked={wipeFirst}
            onCheckedChange={setWipeFirst}
            data-testid="settings-backup-wipe-first"
            className="mt-0.5"
          />
          <div className="text-xs">
            <div className="flex items-center gap-1.5 font-semibold text-amber-800">
              <AlertTriangle className="w-3.5 h-3.5" />
              Wipe-first restore
            </div>
            <p className="text-amber-700/90 mt-0.5">
              When ON, the existing keys, playlists, resellers,
              key↔playlist assignments and devices are <strong>deleted</strong>{" "}
              before the backup is replayed — useful for restoring a clean
              snapshot. Admin accounts are <em>always</em> preserved. When OFF
              (default), records merge by id and only NEW or matching
              records are written.
            </p>
          </div>
        </div>

        {lastResult && (
          <div className="text-xs bg-slate-50 border border-slate-200 rounded-md p-3 font-mono leading-relaxed">
            <div className="flex items-center gap-1.5 text-emerald-700 font-sans font-semibold mb-1">
              <CheckCircle2 className="w-3.5 h-3.5" />
              Last restore
            </div>
            keys_restored: {lastResult.keys_restored}<br />
            playlists_restored: {lastResult.playlists_restored}<br />
            resellers_restored: {lastResult.resellers_restored || 0}<br />
            assignments_restored: {lastResult.assignments_restored || 0}<br />
            devices_restored: {lastResult.devices_restored || 0}<br />
            settings_restored: {String(!!lastResult.settings_restored)}<br />
            wiped_first: {String(lastResult.wiped_first)}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
